import React, { useState } from 'react';
import { X, Save, User, DollarSign, Calendar } from 'lucide-react';

const AddContributionModal = ({ isOpen, onClose, onSubmit, members = [], isLoading = false }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    amount: '',
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    status: 'pending',
    payment_method: 'cash'
  });

  const [errors, setErrors] = useState({});

  const formatAmount = (value) => {
    // Supprimer tout ce qui n'est pas un chiffre
    const numbers = value.replace(/[^\d]/g, '');
    if (!numbers) return '';
    
    // Ajouter des espaces pour les milliers
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const handleAmountChange = (e) => {
    const formatted = formatAmount(e.target.value);
    setFormData(prev => ({
      ...prev,
      amount: formatted
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.user_id) newErrors.user_id = 'Sélectionnez un membre';
    if (!formData.amount) newErrors.amount = 'Saisissez un montant';
    if (!formData.period_month) newErrors.period_month = 'Sélectionnez un mois';
    if (!formData.period_year) newErrors.period_year = 'Saisissez une année';

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Convertir le montant en nombre
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount.replace(/\s/g, ''))
      };
      
      onSubmit(submitData);
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      amount: '',
      period_month: new Date().getMonth() + 1,
      period_year: new Date().getFullYear(),
      status: 'pending',
      payment_method: 'cash'
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const months = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Nouvelle Cotisation</h2>
              <p className="text-sm text-gray-500">Ajouter une cotisation pour un membre</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Membre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="h-4 w-4 inline mr-2" />
              Membre *
            </label>
            <select
              value={formData.user_id}
              onChange={(e) => setFormData(prev => ({...prev, user_id: e.target.value}))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.user_id ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionnez un membre...</option>
              {members.map(member => (
                <option key={member.id} value={member.id}>
                  {member.first_name} {member.last_name} - {member.fraternity}
                </option>
              ))}
            </select>
            {errors.user_id && (
              <p className="mt-1 text-sm text-red-600">{errors.user_id}</p>
            )}
          </div>

          {/* Montant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="h-4 w-4 inline mr-2" />
              Montant (F CFA) *
            </label>
            <input
              type="text"
              placeholder="ex: 30 000"
              value={formData.amount}
              onChange={handleAmountChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.amount ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.amount && (
              <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
            )}
          </div>

          {/* Période */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-2" />
                Mois *
              </label>
              <select
                value={formData.period_month}
                onChange={(e) => setFormData(prev => ({...prev, period_month: parseInt(e.target.value)}))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.period_month ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              {errors.period_month && (
                <p className="mt-1 text-sm text-red-600">{errors.period_month}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Année *
              </label>
              <input
                type="number"
                min="2020"
                max="2030"
                value={formData.period_year}
                onChange={(e) => setFormData(prev => ({...prev, period_year: parseInt(e.target.value)}))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.period_year ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.period_year && (
                <p className="mt-1 text-sm text-red-600">{errors.period_year}</p>
              )}
            </div>
          </div>

          {/* Statut */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({...prev, status: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="pending">En attente</option>
              <option value="paid">Payé</option>
              <option value="overdue">En retard</option>
            </select>
          </div>

          {/* Méthode de paiement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Méthode de paiement
            </label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData(prev => ({...prev, payment_method: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="cash">Espèces</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="bank_transfer">Virement bancaire</option>
              <option value="online">En ligne</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Ajout...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Ajouter</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContributionModal;